import type { MailReward } from './admin-send.types';
import { validateMailReward } from './reward-validation';
import type { MailJobDefinition, MailJobRecipient, MailJobSummary } from './job.types';

const ADDR_RE = /^0x[0-9a-fA-F]{40}$/;

function toRewardArray(rewards: Record<string, number> | undefined, ctx: string): MailReward[] {
  // 비어 있으면 throw. 비어있지 않으면 모든 항목을 validateMailReward 로 검증 —
  // 0/음수 금액을 조용히 버리지 않고(부분 발송 금지) 명확히 에러를 던진다.
  const entries = Object.entries(rewards ?? {});
  if (entries.length === 0) throw new Error(`${ctx}: 보상(reward)이 비어 있음`);
  return entries.map(([key, amount]) => {
    const v = validateMailReward(key, amount);
    if (!v.ok)
      throw new Error(`${ctx}: invalid reward ${key}: ${(v as { reason: string }).reason}`);
    return { key: v.key, amount: v.amount };
  });
}

export function validateMailJobDefinition(input: unknown): {
  recipients: MailJobRecipient[];
  summary: MailJobSummary;
} {
  const def = input as MailJobDefinition;
  if (!def || typeof def.jobId !== 'string' || def.jobId.length === 0)
    throw new Error('jobId 필요');
  if (def.jobId.includes(':')) throw new Error("jobId 에 ':' 문자 금지");
  if (!def.titleKey || !def.contentKey) throw new Error('titleKey/contentKey 필요');
  if (!Number.isInteger(def.ttlDays) || def.ttlDays <= 0 || def.ttlDays > 365)
    throw new Error('ttlDays 는 1~365 사이 정수여야 함');
  if (!Array.isArray(def.rows) || def.rows.length === 0) throw new Error('rows 비어 있음');

  const recipients: MailJobRecipient[] = [];
  const rewardTotals: Record<string, number> = {};
  const seen = new Set<string>();
  let skippedDuplicateAddresses = 0;

  for (const row of def.rows) {
    const account = String(row?.account ?? '');
    if (!ADDR_RE.test(account)) throw new Error(`잘못된 account 형식: ${account}`);
    const lower = account.toLowerCase();
    if (seen.has(lower)) {
      skippedDuplicateAddresses++;
      continue;
    }
    seen.add(lower);
    const rewards = toRewardArray(row.rewards ?? def.defaultRewards, `account ${account}`);
    for (const r of rewards) rewardTotals[r.key] = (rewardTotals[r.key] ?? 0) + r.amount;
    recipients.push({ jobId: def.jobId, account, rewards });
  }

  const summary: MailJobSummary = {
    jobId: def.jobId,
    groupType: def.groupType ?? 'UNSPECIFIED',
    totalRows: def.rows.length,
    recipients: recipients.length,
    skippedDuplicateAddresses,
    rewardTotals,
    titleKey: def.titleKey,
    contentKey: def.contentKey,
  };
  return { recipients, summary };
}
